import {useEffect, useState} from 'react'
import {useApolloQuery} from 'apollo-hooks'
import {UseQueryOptions} from 'apollo-hooks/dist/useQueryBase'

const DEFAULT_QUERY_POLL_INTERVAL = 5000

/**
 * A hook that wraps useApolloQuery and adds conditional polling.
 * Set queryOpts.pollInterval to the desired polling interval. By default, it polls every 5 seconds.
 * Set shouldPoll to a function that returns true if polling should continue. By Default, it polls until the query result is truthy.
 */
export default function useQueryWithConditionalPolling<TData, TVariables>(
  queryOpts: UseQueryOptions<TData, TVariables>,
  shouldPoll: (queryResult: TData) => boolean = queryResult => !queryResult
) {
  const {pollInterval = DEFAULT_QUERY_POLL_INTERVAL} = queryOpts
  const {data, refetch} = useApolloQuery<TData, TVariables>({
    ...queryOpts,
    pollInterval: null
  })
  const [isPolling, setIsPolling] = useState<NodeJS.Timeout>()

  useEffect(() => {
    if (shouldPoll(data) && !isPolling && pollInterval) {
      const intervalId = setInterval(() => {
        refetch()
      }, pollInterval)

      setIsPolling(intervalId)
    }

    return () => {
      if (isPolling) {
        clearInterval(isPolling)
      }
    }
  }, [data, isPolling])

  return {data, refetch}
}
